import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface NotificationRow {
  id: string;
  title: string;
  type: string;
  created_at: string;
}

export interface FullNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  related_id: string | null;
  related_type: string | null;
}

/**
 * All notifications for a user, newest first. Used by the /notifications
 * page which supports mark-as-read + dismiss flows. Callers that mutate
 * notification state should use queryClient.setQueryData against this key
 * for optimistic updates, then invalidate on settle.
 */
export function useAllNotifications(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["notifications", userId, "all"],
    enabled: Boolean(userId),
    staleTime: 10_000,
    queryFn: async (): Promise<FullNotification[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, message, type, is_read, created_at, related_id, related_type")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        // MS397: cap history at 200 — the /notifications page shows a
        // full log and 200 entries is more than any user will scroll.
        .limit(200);
      if (error) throw error;
      return (data as FullNotification[] | null) ?? [];
    },
  });
}

/**
 * Unread notification count for the sidebar badge. staleTime is short (5s)
 * because the badge is directly visible to the user and stale-ness here
 * looks like a bug. Callers that mark notifications read should also
 * invalidate the matching query key so the badge updates immediately.
 */
export function useUnreadNotificationCount(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["notifications", userId, "unread-count"],
    enabled: Boolean(userId),
    staleTime: 5_000,
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("is_read", false);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

/**
 * Recent notifications for the dashboard activity feed. Dedupes by title so
 * repeated "Welcome to Lembra" notifications don't flood the list. Short
 * staleTime so fresh events appear quickly when the user returns.
 */
export function useRecentNotifications(
  userId: string | null | undefined,
  limit = 10,
) {
  return useQuery({
    queryKey: ["notifications", userId, "recent", limit],
    enabled: Boolean(userId),
    staleTime: 10_000,
    queryFn: async (): Promise<NotificationRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, type, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;

      const rows = (data as NotificationRow[] | null) ?? [];
      const seen = new Set<string>();
      return rows.filter((n) => {
        if (seen.has(n.title)) return false;
        seen.add(n.title);
        return true;
      });
    },
  });
}
